/**
 * Formularlogik ohne Oberfläche.
 *
 * Bewusst frei von `document`: Die Tests laufen in Node, und die Umwandlung
 * zwischen Tauchgang und Formularwerten ist der Teil, der stimmen muss.
 */

import { SPEICHER, alleAus } from './db.js';
import { FELDER, FLASCHE } from './felder.js';
import { inhaltsHash } from './garmin.js';
import { ortszeit } from './format.js';
import { neueId, speichere } from './saetze.js';

/**
 * Die Ortszeit eines Tauchgangs als Datum und Uhrzeit fürs Formular.
 *
 * `ortszeit()` liefert einen Zeitpunkt, dessen UTC-Felder die Ortszeit tragen —
 * deshalb wird hier mit getUTC* gelesen und nicht mit getHours().
 */
export function ausStartUtc(tg) {
  const zeit = ortszeit(tg);
  if (!zeit) return { datum: '', zeit: '' };
  const zwei = (n) => String(n).padStart(2, '0');
  return {
    datum: `${zeit.getUTCFullYear()}-${zwei(zeit.getUTCMonth() + 1)}-${zwei(zeit.getUTCDate())}`,
    zeit: `${zwei(zeit.getUTCHours())}:${zwei(zeit.getUTCMinutes())}`,
  };
}

/**
 * Datum und Uhrzeit zurück in `start_utc`.
 *
 * Mit Versatz entsteht ein echter UTC-Zeitpunkt, ohne Versatz eine naive
 * Ortszeit — genau die zwei Formen, die im Bestand vorkommen. Ein neu
 * erfasster Tauchgang hat keinen Versatz und bleibt deshalb naiv.
 */
export function zuStartUtc(datum, zeit, versatz) {
  if (!datum || !zeit) return null;
  if (typeof versatz !== 'number') return `${datum}T${zeit}:00`;
  const alsUtc = Date.parse(`${datum}T${zeit}:00Z`);
  return new Date(alsUtc - versatz * 60000).toISOString();
}

/** Aus einem Tauchgang die Werte fürs Formular. */
export function zuFormular(tg) {
  const quelle = tg ?? {};
  const { datum, zeit } = ausStartUtc(quelle);
  const werte = { start_datum: datum, start_zeit: zeit };

  for (const f of FELDER) {
    if (f.schluessel in werte) continue;
    if (f.schluessel === 'dauer_min') {
      werte.dauer_min = typeof quelle.duration_s === 'number'
        ? Math.round(quelle.duration_s / 60) : '';
      continue;
    }
    if (f.schluessel === 'buddies') {
      werte.buddies = Array.isArray(quelle.buddies) ? quelle.buddies.join(', ') : '';
      continue;
    }
    if (f.schluessel in FLASCHE) {
      werte[f.schluessel] = quelle.tanks?.[0]?.[FLASCHE[f.schluessel]] ?? '';
      continue;
    }
    werte[f.schluessel] = quelle[f.schluessel] ?? '';
  }
  return werte;
}

const leer = (wert) => wert === '' || wert === null || wert === undefined;

function feldIstZahl(schluessel) {
  return FELDER.find((f) => f.schluessel === schluessel)?.art === 'zahl';
}

/**
 * Aus den Formularwerten wieder ein Tauchgang.
 *
 * `bisher` wird kopiert und nur um die bekannten Felder ergänzt. Alles, was das
 * Formular nicht kennt — Herzfrequenz, CNS, Tauchgangsart, Herkunft —, bleibt
 * dadurch stehen. Ohne diese Regel leerte ein Speichern den halben Datensatz.
 */
/**
 * Nimmt das Formular die Pruefmarke weg?
 *
 * Der Import setzt „zu pruefen" mit einem Grund — etwa einem Flaschendruck,
 * der nicht sein kann. Wer genau den korrigiert, hat die Sache erledigt; dass
 * die Marke danach stehen blieb, war der Punkt, an dem es auffiel. Sie
 * verschwindet aber nicht heimlich beim Speichern: Das Formular zeigt den
 * Grund und ein Haekchen daneben, und wer es abwaehlt, behaelt die Marke.
 *
 * Als reine Funktion, damit die Regel ohne DOM pruefbar ist.
 */
export function nimmtPruefmarke(werte, bisher) {
  if (!bisher?.needs_review) return false;
  return werte.pruefung_erledigt !== false;
}

export function ausFormular(werte, bisher) {
  const tg = { ...(bisher ?? {}) };
  if (nimmtPruefmarke(werte, bisher)) {
    tg.needs_review = 0;
    tg.review_note = null;
  }
  const versatz = typeof tg.tz_offset_min === 'number' ? tg.tz_offset_min : null;

  const start = zuStartUtc(werte.start_datum, werte.start_zeit, versatz);
  if (start) tg.start_utc = start;

  if (!leer(werte.dauer_min)) tg.duration_s = Math.round(Number(werte.dauer_min) * 60);
  tg.buddies = String(werte.buddies ?? '').split(',')
    .map((name) => name.trim()).filter(Boolean);

  for (const f of FELDER) {
    const s = f.schluessel;
    if (['start_datum', 'start_zeit', 'dauer_min', 'buddies'].includes(s)) continue;
    if (s in FLASCHE) continue;
    if (leer(werte[s])) {
      // Nur gepflegte Felder loeschen. Importbestand wird uebersprungen.
      if (f.gepflegt) {
        tg[s] = null;
      }
    } else {
      tg[s] = f.art === 'zahl' ? Number(werte[s]) : werte[s];
    }
  }

  const flasche = { ...(tg.tanks?.[0] ?? {}) };
  let hatWertAusFormular = false;
  for (const [formular, imSatz] of Object.entries(FLASCHE)) {
    if (!leer(werte[formular])) {
      // Hat einen Wert — setzen
      flasche[imSatz] = feldIstZahl(formular) ? Number(werte[formular]) : werte[formular];
      hatWertAusFormular = true;
    }
    // Leere Tank-Felder werden uebersprungen: Flaschenfelder sind Importbestand.
  }
  if (hatWertAusFormular || tg.tanks?.length) {
    const weitere = (tg.tanks ?? []).slice(1);
    tg.tanks = [flasche, ...weitere];
  }

  if (!bisher) {
    tg.source = 'app';
    tg.has_profile = false;
  }
  return tg;
}

const PFLICHT = ['start_datum', 'start_zeit', 'dauer_min', 'max_depth_m'];

/**
 * Prüfen, ohne zu bevormunden.
 *
 * Blockierend ist nur, was einen Tauchgang unidentifizierbar machte: Die
 * Pflichtfelder sind genau die drei Grössen, aus denen die Pipeline den
 * `content_hash` bildet. Alles andere ist ein Hinweis — Nr. 617 mit 300 m
 * Tiefe ist ein echter Eintrag, nur ein falscher, und muss erfassbar bleiben.
 * Die Grenzen stammen aus `src/tauchlogbuch/plausibilitaet.py`.
 */
export function pruefe(werte, vergebeneNummern = [], jetzt = new Date()) {
  const fehler = [];
  const hinweise = [];

  for (const schluessel of PFLICHT) {
    if (leer(werte[schluessel])) {
      fehler.push({ feld: schluessel, text: 'Pflichtangabe' });
    }
  }

  const nummer = Number(werte.dive_number);
  if (!leer(werte.dive_number) && vergebeneNummern.includes(nummer)) {
    fehler.push({ feld: 'dive_number', text: `Nummer ${nummer} ist schon vergeben.` });
  }

  // Grenzen wortgleich aus plausibilitaet.py: TIEFE_M = (0.5, 150.0),
  // DAUER_S = (60, 4 * 3600). Beide Richtungen, sonst rutschte eine 0 durch.
  const tiefe = Number(werte.max_depth_m);
  if (!leer(werte.max_depth_m) && (tiefe > 150 || tiefe < 0.5)) {
    hinweise.push({ feld: 'max_depth_m', text: `${tiefe} m liegt ausserhalb von 0.5–150 m.` });
  }
  const dauer = Number(werte.dauer_min);
  if (!leer(werte.dauer_min) && (dauer > 240 || dauer < 1)) {
    hinweise.push({ feld: 'dauer_min', text: `${dauer} min liegt ausserhalb von 1–240 min.` });
  }
  if (!leer(werte.tank_start_bar) && !leer(werte.tank_end_bar)
      && Number(werte.tank_end_bar) > Number(werte.tank_start_bar)) {
    hinweise.push({ feld: 'tank_end_bar', text: 'Enddruck liegt über dem Anfangsdruck.' });
  }
  if (!leer(werte.start_datum)) {
    const start = Date.parse(`${werte.start_datum}T00:00:00Z`);
    if (Number.isFinite(start) && start > jetzt.getTime() + 86400000) {
      hinweise.push({ feld: 'start_datum', text: 'Das Datum liegt in der Zukunft.' });
    }
  }
  return { fehler, hinweise };
}

/**
 * Die nächste freie Nummer.
 *
 * Die Nummerierung ist laut CLAUDE.md global fortlaufend und lueckenlos —
 * deshalb wird oben angebaut und nicht die kleinste Luecke gesucht.
 */
export function naechsteNummer(tauchgaenge) {
  const hoechste = tauchgaenge
    .map((tg) => Number(tg.dive_number))
    .filter((n) => Number.isFinite(n))
    .reduce((a, b) => Math.max(a, b), 0);
  return hoechste + 1;
}

export function vergebeneNummern(tauchgaenge, ausserId = null) {
  return tauchgaenge
    .filter((tg) => tg.id !== ausserId)
    .map((tg) => Number(tg.dive_number))
    .filter((n) => Number.isFinite(n));
}

const schluesselform = (name) => String(name ?? '').trim().toLowerCase();

/**
 * Einen Stammsatz nach Namen finden oder anlegen.
 *
 * Verglichen wird ohne Rücksicht auf Gross- und Kleinschreibung und ohne
 * Leerzeichen am Rand — sonst entstünde neben "Blue Hole" bald ein
 * "blue hole ".
 */
export async function findeOderLege(db, speicher, name, optionen) {
  const gesucht = schluesselform(name);
  if (!gesucht) return { id: null, neu: false };
  const vorhanden = (await alleAus(db, speicher))
    .find((satz) => schluesselform(satz.name) === gesucht);
  if (vorhanden) return { id: vorhanden.id, neu: false };

  const id = neueId();
  await speichere(db, speicher, { id, name: String(name).trim() }, optionen);
  return { id, neu: true };
}

/**
 * Das Formular speichern.
 *
 * Stammdaten zuerst: Schlägt das Anlegen fehl, wird der Tauchgang nicht
 * geschrieben — er zeigte sonst auf einen Platz, den es nicht gibt.
 */
/**
 * Der Dedup-Schluessel eines von Hand erfassten Tauchgangs.
 *
 * Ohne ihn erkennt der Garmin-Import den Satz nicht wieder: Er vergleicht ueber
 * `content_hash`, und ein leeres Feld zaehlt als unbekannt — wer den Tauchgang
 * auf dem Boot eintraegt und die Garmin-Datei spaeter einliest, haette ihn
 * zweimal.
 *
 * Nur fuer Saetze der App. Was aus einer Quelle kam, behaelt seinen Hash: Er
 * wird beim Import einmal berechnet und eingefroren (CLAUDE.md, B). Umgekehrt
 * darf der Hash eines App-Satzes nicht auf der ersten Eingabe stehen bleiben —
 * er beschreibt, was jetzt dasteht.
 */
async function ergaenzeHash(tg, werte) {
  if (tg.source && tg.source !== 'app') return;
  const lokal = `${werte.start_datum}T${werte.start_zeit}`;
  const dauer = tg.duration_s;
  const tiefe = tg.max_depth_m;
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(lokal)) return;
  if (typeof dauer !== 'number' || typeof tiefe !== 'number') return;
  tg.content_hash = await inhaltsHash(lokal, dauer, tiefe);
}

export async function speichereFormular(db, werte, bisher, optionen = {}) {
  const platz = await findeOderLege(db, SPEICHER.sites, werte.site_name, optionen);
  const reise = await findeOderLege(db, SPEICHER.trips, werte.trip_name, optionen);

  const tg = ausFormular(werte, bisher);
  tg.id = bisher?.id ?? neueId();
  await ergaenzeHash(tg, werte);
  if (platz.id) tg.site_id = platz.id;
  if (reise.id) tg.trip_id = reise.id;

  await speichere(db, SPEICHER.dives, tg, optionen);
  return { id: tg.id, neuerPlatz: platz.neu, neueReise: reise.neu };
}
